import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, User, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { apiClient } from '../utils/api-client';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  userName: string;
  onUploadSuccess: (signedId: string) => void;
  disabled?: boolean;
  token: string;
}

export function AvatarUpload({
  currentAvatarUrl,
  userName,
  onUploadSuccess,
  disabled = false,
  token
}: AvatarUploadProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Get API origin configuration
  const getApiOrigin = useCallback(() => {
    const baseURL = apiClient.getBaseURL();

    if (!baseURL) {
      // Fallback to current origin if no base URL is configured
      const fallbackOrigin = {
        host: window.location.hostname,
        port: window.location.port || undefined,
        protocol: window.location.protocol.replace(':', '')
      };
      return fallbackOrigin;
    }

    try {
      const url = new URL(baseURL);
      const origin = {
        host: url.hostname,
        port: url.port || undefined,
        protocol: url.protocol.replace(':', '')
      };
      return origin;
    } catch (error) {
      console.error('Invalid base URL:', baseURL, error);
      // Fallback to current origin
      const fallbackOrigin = {
        host: window.location.hostname,
        port: window.location.port || undefined,
        protocol: window.location.protocol.replace(':', '')
      };
      return fallbackOrigin;
    }
  }, []);

  const handleUploadSuccess = useCallback((signedIds: string[]) => {
    if (signedIds.length > 0) {
      onUploadSuccess(signedIds[0]);
      setPreviewUrl(null);
      setUploading(false);

      // Don't show toast here - let the parent component handle it
      // after the profile update is complete
    }
  }, [onUploadSuccess]);

  const handleFileSelect = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const file = fileArray[0];

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: t('profile.invalidFileType'),
        description: t('profile.pleaseSelectImage'),
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: t('profile.fileTooLarge'),
        description: t('profile.maxFileSize'),
        variant: 'destructive',
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    return fileArray;
  }, [t, toast]);

  const clearPreview = useCallback(() => {
    setPreviewUrl(null);
    setUploading(false);
  }, []);

  const getCurrentImageUrl = () => {
    if (previewUrl) return previewUrl;
    if (currentAvatarUrl) return currentAvatarUrl;
    return null;
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <Avatar className="h-24 w-24">
          <AvatarImage
            src={getCurrentImageUrl() || undefined}
            alt={userName}
            className="object-cover"
          />
          <AvatarFallback className="text-lg">
            {userName
              ?.split(' ')
              ?.map(name => name[0])
              ?.join('')
              ?.toUpperCase()
              ?.slice(0, 2) || <User className="h-8 w-8" />}
          </AvatarFallback>
        </Avatar>

        {previewUrl && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
            onClick={clearPreview}
            disabled={uploading}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      <DirectUploadProvider
        onSuccess={handleUploadSuccess}
        origin={getApiOrigin()}
        headers={{
          'Authorization': `Bearer ${token}`,
        }}
        render={({ handleUpload, uploads, ready }) => {
          const isUploading = uploads.some(upload => upload.state === 'uploading');
          const hasError = uploads.some(upload => upload.state === 'error');

          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files) {
              const processedFiles = handleFileSelect(files);
              if (processedFiles) {
                handleUpload(processedFiles);
              }
            }
            // Reset so the same file can be selected again
            e.target.value = '';
          };

          return (
            <div className="flex flex-col items-center space-y-2">
              {/* Selfie capture (front camera) */}
              <input
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleChange}
                className="hidden"
                id="avatar-capture"
                disabled={!ready || disabled || uploading}
              />
              {/* Gallery / filesystem */}
              <input
                type="file"
                accept="image/*"
                onChange={handleChange}
                className="hidden"
                id="avatar-upload"
                disabled={!ready || disabled || uploading}
              />

              <div className="flex gap-2">
                <label htmlFor="avatar-capture">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!ready || disabled || uploading}
                    className="cursor-pointer"
                    asChild
                  >
                    <span>
                      {isUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Camera className="h-4 w-4" />
                      )}
                    </span>
                  </Button>
                </label>

                <label htmlFor="avatar-upload">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!ready || disabled || uploading}
                    className="cursor-pointer"
                    asChild
                  >
                    <span>
                      {isUploading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('profile.uploading')}
                        </>
                      ) : (
                        <>
                          <User className="mr-2 h-4 w-4" />
                          {currentAvatarUrl ? t('profile.changeAvatar') : t('profile.uploadAvatar')}
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>

              {uploads.map((upload) => {
                switch (upload.state) {
                  case 'uploading':
                    return (
                      <div key={upload.id} className="text-sm text-muted-foreground">
                        {t('profile.uploadingProgress', { progress: upload.progress })}
                      </div>
                    );
                  case 'error':
                    return (
                      <div key={upload.id} className="text-sm text-destructive">
                        {t('profile.uploadError')}: {upload.error}
                      </div>
                    );
                  default:
                    return null;
                }
              })}

              {hasError && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                  className="text-xs"
                >
                  {t('profile.retryUpload')}
                </Button>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
